import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@dotfile/frontend/shared/common';
import {
  Button,
  Modal,
  ModalOverlay,
  useDisclosure,
  useIsMobile,
} from '@dotfile/frontend/shared/design-system';
import { ClientPortalChecks_ElectronicSignatureCheck } from '@dotfile/shared/data-access-client-portal';

import {
  ContactAuthContext,
  SupportButton,
  useLatestClientPortalVersion,
} from '../../../shared';
import {
  CheckCard,
  CheckDescription,
  CheckHeading,
  CheckReview,
  PendingStatuses,
} from '../shared';
import { useCheckModalWithQueryParam } from '../shared/hooks/use-modal-with-query-param';
import { InviteSignerModalContent } from './components/invite-signer-modal-content';
import { useRefreshUrl, useRequestSignature } from './hooks';

type ElectronicSignatureCheckProps = {
  check: ClientPortalChecks_ElectronicSignatureCheck;
};

export const ElectronicSignatureCheck = ({
  check,
}: ElectronicSignatureCheckProps) => {
  const { t } = useTranslation();
  const { auth } = useAuth<ContactAuthContext>();
  const { onOpen } = useCheckModalWithQueryParam(check.id);
  const { data } = useLatestClientPortalVersion({
    // If the data is available, it must already be in the cache
    fetchPolicy: 'cache-only',
  });
  const supportEmail =
    data?.latestClientPortalVersion?.setting?.supportEmail ??
    'support@dotfile.com';

  const {
    isOpen: isInviteSignerModalOpen,
    onOpen: onInviteSignerModalOpen,
    onClose: onInviteSignerModalClose,
  } = useDisclosure();
  const isMobile = useIsMobile();

  const { requestSignature, error } = useRequestSignature();
  const { refreshUrl } = useRefreshUrl();
  const [isLoading, setIsLoading] = useState(false);

  const hasSignatureLink = !!check.data?.vendor?.signatureLink;
  const isSignerTheConnectedUser = check.individual?.id === auth.contactId;

  const handleSignDocument = useCallback(async () => {
    if (isSignerTheConnectedUser) {
      // Signer IS connected user: request signature directly
      setIsLoading(true);
      let success = false;
      if (hasSignatureLink) {
        success = await refreshUrl({ checkId: check.id });
      } else {
        success = await requestSignature({ checkId: check.id });
      }

      if (success) {
        onOpen();
      }
      setIsLoading(false);
    } else {
      // Signer is NOT connected user: show invite modal
      onInviteSignerModalOpen();
    }
  }, [
    hasSignatureLink,
    isSignerTheConnectedUser,
    onOpen,
    requestSignature,
    refreshUrl,
    check.id,
    onInviteSignerModalOpen,
  ]);

  const getButtonText = () => {
    if (isSignerTheConnectedUser) {
      return t('checks.electronic_signature.sign', {
        defaultValue: 'Sign',
        ns: 'client-portal',
      });
    } else {
      return t('checks.electronic_signature.request_to_sign', {
        defaultValue: 'Request to sign',
        ns: 'client-portal',
      });
    }
  };

  return (
    <>
      <CheckCard
        heading={<CheckHeading check={check} />}
        description={<CheckDescription check={check} />}
        action={
          error ? (
            <SupportButton supportEmail={supportEmail} />
          ) : (
            PendingStatuses.includes(check.status) && (
              <Button
                variant="solid"
                colorScheme="custom"
                onClick={handleSignDocument}
                isLoading={isLoading}
                loadingText={t('common.loading', {
                  defaultValue: 'Loading...',
                  ns: 'client-portal',
                })}
              >
                {getButtonText()}
              </Button>
            )
          )
        }
        review={<CheckReview check={check} />}
      />

      <Modal
        variant="dialog-white"
        isOpen={isInviteSignerModalOpen}
        onClose={onInviteSignerModalClose}
        size={isMobile ? 'full' : '2xl'}
        isCentered
        scrollBehavior="inside"
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay />
        {check.individual && (
          <InviteSignerModalContent
            individual={check.individual}
            onClose={onInviteSignerModalClose}
          />
        )}
      </Modal>
    </>
  );
};
